import { COLOR_MODE_STORAGE_KEY } from '@/constants/color-mode.constant'
import type { ColorMode } from '@/types/color-mode.type'
import { isPublicPageRoute } from '@/utils/isPublicPageRoute.util'

export function isColorMode(value: unknown): value is ColorMode {
    return value === 'light' || value === 'dark'
}

export function getStoredColorMode(): ColorMode {
    if (typeof window === 'undefined') {
        return 'light'
    }

    try {
        const raw = window.localStorage.getItem(COLOR_MODE_STORAGE_KEY)
        return isColorMode(raw) ? raw : 'light'
    } catch {
        return 'light'
    }
}

export function setStoredColorMode(mode: ColorMode): void {
    if (typeof window === 'undefined') {
        return
    }

    try {
        window.localStorage.setItem(COLOR_MODE_STORAGE_KEY, mode)
    } catch {
        // Ignore quota / private-mode failures
    }
}

function isExcludedAuthenticatedShellPath(pathname: string | null): boolean {
    return pathname === '/select-company'
}

/** Welcome + signup/onboarding: dark follows stored preference for guests and signed-in users. */
const AUTH_MARKETING_DARK_ROUTES = ['^/$', '^/auth/signup$', '^/onboarding', '^/signup'] as const

export function isAuthMarketingDarkRoute(pathname: string | null): boolean {
    if (!pathname) return false
    return AUTH_MARKETING_DARK_ROUTES.some((route) => new RegExp(route).test(pathname))
}

/** Public routes where dark mode applies for everyone (including guests). */
const PUBLIC_ROUTES_ALLOWING_DARK = ['^/(.*?)/interview/feedback'] as const

/** Public for guests, but authenticated users still get the app shell + dark mode. */
const PUBLIC_ROUTES_ALLOWING_AUTH_DARK = ['^/(.*?)/recruitment/create-jd$'] as const

function isPublicRouteAllowingDark(pathname: string | null): boolean {
    if (!pathname) return false
    return PUBLIC_ROUTES_ALLOWING_DARK.some((route) => new RegExp(route).test(pathname))
}

function isPublicRouteAllowingAuthDark(pathname: string | null): boolean {
    if (!pathname) return false
    return PUBLIC_ROUTES_ALLOWING_AUTH_DARK.some((route) => new RegExp(route).test(pathname))
}

/**
 * Dark mode applies on non-public app routes when the user prefers dark, and on
 * auth marketing routes (welcome / onboarding / signup) for guests and signed-in users.
 * `hasNoUser` is true only after we know the session has no user — while auth is
 * still loading, keep dark to avoid a light flash once ColorModeProvider applies.
 * Dual-use routes like create-jd stay light for guests but allow dark when signed in.
 * Interview feedback allows dark for everyone (panelist token links need no login).
 */
export function shouldApplyDark({
    preference,
    pathname,
    hasNoUser,
}: {
    preference: ColorMode
    pathname: string | null
    /** True when auth resolved and there is no signed-in user. */
    hasNoUser: boolean
}): boolean {
    if (preference !== 'dark') {
        return false
    }

    if (isPublicRouteAllowingDark(pathname) || isAuthMarketingDarkRoute(pathname)) {
        return true
    }

    if (hasNoUser) {
        return false
    }

    if (isExcludedAuthenticatedShellPath(pathname)) {
        return false
    }

    if (isPublicPageRoute(pathname)) {
        return isPublicRouteAllowingAuthDark(pathname)
    }

    return true
}

export function applyHtmlDarkClass(apply: boolean): void {
    if (typeof document === 'undefined') {
        return
    }

    document.documentElement.classList.toggle('dark', apply)
}
